/**
 * Entra ID (Azure AD) single sign-on — SPA + Authorization Code + PKCE via MSAL.
 *
 * App registration: EmailCreativeStudio_Dev (single-tenant), SHARED by dev, qa
 * and uat; prod gets its own registration and overrides clientId/tenantId
 * through runtime config. Public client, PKCE, NO client secret — everything in
 * this bundle is public.
 *
 * Sign-in requests OIDC scopes only. An ACCESS token for the backend is
 * acquired separately (getAccessToken) and only once an API scope is configured
 * — see authConfig.API_SCOPE.
 *
 * Identity comes from the TOKEN's claims, never from user input, so authorship
 * cannot be spoofed; `oid` is the immutable user id.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  EventType,
  type AccountInfo,
  type AuthenticationResult,
} from "@azure/msal-browser";
import {
  API_SCOPE,
  AUTHORITY,
  AUTH_ENABLED,
  CLIENT_ID,
  REDIRECT_URI,
} from "./authConfig";

/** OIDC scopes — always safe to request; no admin consent beyond the defaults. */
export const OIDC_SCOPES = ["openid", "profile", "email"];

/** Sign-in request. The API scope joins in only when one is configured. */
export const LOGIN_REQUEST = {
  scopes: API_SCOPE ? [...OIDC_SCOPES, API_SCOPE] : OIDC_SCOPES,
};

/**
 * Singleton, created ONLY when this environment enables auth — so an env with
 * AUTH_ENABLED unset never contacts Microsoft and the app behaves exactly as it
 * does today.
 */
export const msalInstance: PublicClientApplication | null = AUTH_ENABLED
  ? new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY, // single-tenant, NOT /common
        redirectUri: REDIRECT_URI, // must be a registered SPA redirect URI
        postLogoutRedirectUri: REDIRECT_URI,
        navigateToLoginRequestUrl: true,
      },
      // Per-tab: nothing persists once the tab closes (shared-machine safety).
      cache: { cacheLocation: "sessionStorage" },
    })
  : null;

let authError: string | null = null;
/** Sign-in failure message, if any — rendered by <AuthGate>. */
export const getAuthError = (): string | null => authError;

/**
 * Call ONCE before rendering. Completes the redirect round-trip back from
 * Microsoft and establishes the active account. Never throws: a failure here
 * surfaces as an error screen, not a blank page.
 */
export async function initAuth(): Promise<void> {
  if (!msalInstance) return;
  try {
    await msalInstance.initialize();

    const result = await msalInstance.handleRedirectPromise();
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    } else if (!msalInstance.getActiveAccount()) {
      const [first] = msalInstance.getAllAccounts();
      if (first) msalInstance.setActiveAccount(first);
    }

    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        if (payload.account) msalInstance!.setActiveAccount(payload.account);
      }
    });
  } catch (err) {
    authError = err instanceof Error ? err.message : String(err);
  }
}

/** Full-page redirect to Microsoft. AADSTS50011 ⇒ redirect URI not registered. */
export function signIn(): void {
  void msalInstance?.loginRedirect(LOGIN_REQUEST);
}

/**
 * FULL Microsoft sign-out, not just a local cache clear — on a shared machine a
 * local-only clear lets SSO silently resume the same account.
 */
export function signOut(): void {
  void msalInstance?.logoutRedirect();
}

/**
 * Access token for backend calls, or null when there is nothing to send.
 *
 * Returns null while no API scope is configured — the app registration has no
 * Application ID URI yet, so there is no audience the backend could validate.
 * Silent first; if the token needs interaction, renew by full-page redirect.
 */
export async function getAccessToken(): Promise<string | null> {
  if (!msalInstance || !API_SCOPE) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  try {
    const res = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account,
    });
    return res.accessToken || null;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ scopes: [API_SCOPE], account });
    }
    return null;
  }
}

export type CurrentUser = {
  /** Immutable Entra object id — the stable user key. */
  oid: string;
  name: string;
  email: string | null;
};

/**
 * Map an MSAL account to the app's user shape. Pure — unit-tested directly,
 * since Conditional Access makes automated browser sign-in untestable.
 */
export function mapAccountToUser(account: AccountInfo): CurrentUser {
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>;
  const oid = typeof claims.oid === "string" ? claims.oid : account.localAccountId;
  const rawEmail =
    (typeof claims.email === "string" && claims.email) ||
    (typeof claims.preferred_username === "string" && claims.preferred_username) ||
    account.username ||
    "";
  const email = rawEmail.includes("@") ? rawEmail.toLowerCase() : null;
  const name =
    account.name?.trim() || (email ? email.split("@")[0] : "Unknown user");
  return { oid, name, email };
}

/** The signed-in user, or null. Null whenever auth is disabled. */
export function getCurrentUser(): CurrentUser | null {
  const account = msalInstance?.getActiveAccount();
  return account ? mapAccountToUser(account) : null;
}
